import { useState, useEffect } from 'react';
import { TokenState } from '@/components/tabletop/types';

interface TokenEditModalProps {
  token: TokenState | null;
  onSave: (id: string, text: string, color: string) => void;
  onClose: () => void;
}

const PRESET_COLORS = [
  { name: 'Red', value: '#ff6b6b' },
  { name: 'Orange', value: '#ffa94d' },
  { name: 'Yellow', value: '#ffd43b' },
  { name: 'Green', value: '#51cf66' },
  { name: 'Blue', value: '#4dabf7' },
  { name: 'Purple', value: '#9775fa' },
  { name: 'Pink', value: '#f06595' },
  { name: 'Gray', value: '#868e96' },
];

export default function TokenEditModal({
  token,
  onSave,
  onClose
}: TokenEditModalProps) {
  const [text, setText] = useState('');
  const [color, setColor] = useState('#ff6b6b');

  useEffect(() => {
    if (token) {
      setText(token.text);
      setColor(token.color);
    }
  }, [token]);

  if (!token) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.length > 7) {
      alert('Text must be 7 characters or less');
      return;
    }
    onSave(token.id, text, color);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 w-96">
        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
          Edit Token
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Text (max 7 characters)
            </label>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 7))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              placeholder="e.g., 10, Poison"
              maxLength={7}
              autoFocus
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Color
            </label>
            <div className="grid grid-cols-4 gap-2">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setColor(preset.value)}
                  className={`h-10 rounded-md border-2 ${
                    color === preset.value
                      ? 'border-blue-500 ring-2 ring-blue-300'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  style={{ backgroundColor: preset.value }}
                  aria-label={preset.name}
                />
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Custom Color
            </label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-full h-10 rounded-md cursor-pointer"
            />
          </div>

          <div className="mb-4">
            <div className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Preview
            </div>
            <div className="flex items-center justify-center">
              <div
                className="rounded-full shadow-md flex items-center justify-center border-2"
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: color,
                  borderColor: adjustColorBrightness(color, -20)
                }}
              >
                <span
                  className="font-bold text-white select-none"
                  style={{
                    fontSize: '24px',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {text}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Helper function to adjust color brightness
function adjustColorBrightness(color: string, percent: number): string {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const adjust = (value: number) => Math.max(0, Math.min(255, value + percent));

  const newR = adjust(r).toString(16).padStart(2, '0');
  const newG = adjust(g).toString(16).padStart(2, '0');
  const newB = adjust(b).toString(16).padStart(2, '0');

  return `#${newR}${newG}${newB}`;
}
